import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { calculatePlayerStats, type PlayerGameStats } from '@/lib/statsCalculator';
import type { Player, Award, LeagueStat, BallArsenalItem } from '@/types';
import { SEASON, isCurrentSeason, isRecapSeason, avgLabelFor } from '@/config/season';
import { WeekLines } from '@/components/WeekLines';
import {
  GraduationCap,
  Target,
  Medal,
  TrendingUp,
  Flame,
  Video,
  MessageCircleQuestion,
  Mail,
  ShieldCheck,
  CircleDot,
  BarChart3,
  Trophy,
} from 'lucide-react';

// ---------- helpers ----------

const youtubeEmbed = (url: string): string | null => {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

const HAND_LABEL: Record<string, string> = {
  right: 'Right-handed',
  left: 'Left-handed',
  'two-handed': 'Two-handed',
};

const ROLE_LABEL: Record<BallArsenalItem['role'], string> = {
  benchmark: 'Benchmark',
  'heavy-oil': 'Heavy Oil',
  medium: 'Medium',
  burn: 'Burn / Dry',
  spare: 'Spare',
  situational: 'Situational',
};

// ---------- small building blocks ----------

const SectionTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="text-willard-taupe">{icon}</span>
    <h2 className="font-heading text-3xl tracking-wide text-willard-black uppercase">{children}</h2>
    <div className="flex-1 h-px bg-willard-grey-200" />
  </div>
);

const BigStat: React.FC<{ label: string; value: React.ReactNode; sub?: string; dark?: boolean }> = ({
  label,
  value,
  sub,
  dark,
}) => (
  <div className={`rounded-xl p-5 text-center ${dark ? 'bg-willard-grey-800' : 'bg-white shadow-tiger'}`}>
    <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${dark ? 'text-willard-taupe' : 'text-willard-grey-500'}`}>
      {label}
    </p>
    <p className={`font-display text-4xl ${dark ? 'text-white' : 'text-willard-black'}`}>{value}</p>
    {sub && <p className={`text-xs mt-1 ${dark ? 'text-willard-grey-400' : 'text-willard-grey-500'}`}>{sub}</p>}
  </div>
);

const Sparkline: React.FC<{ scores: number[] }> = ({ scores }) => {
  if (scores.length < 2) return null;
  const w = 560;
  const h = 96;
  const pad = 6;
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const span = Math.max(max - min, 1);
  const pts = scores.map((s, i) => {
    const x = pad + (i * (w - pad * 2)) / (scores.length - 1);
    const y = h - pad - ((s - min) * (h - pad * 2)) / span;
    return `${x},${y}`;
  });
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none" aria-label="Score trend">
      <polyline points={pts.join(' ')} fill="none" stroke="#c3c0be" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => {
        const [x, y] = p.split(',').map(Number);
        return <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 5 : 2.5} fill={i === pts.length - 1 ? '#ffffff' : '#c3c0be'} />;
      })}
    </svg>
  );
};

// Season type comes from LeagueSecretary's Fall/Summer/Winter/Spring tag on each
// leagueStats row's season string ("Fall 2025"). frac sorts seasons on a timeline.
const SEASON_META: Record<string, { label: string; frac: number; chip: string }> = {
  fall: { label: 'Fall', frac: 0.72, chip: 'bg-amber-100 text-amber-800' },
  winter: { label: 'Winter', frac: 0.95, chip: 'bg-sky-100 text-sky-800' },
  spring: { label: 'Spring', frac: 0.25, chip: 'bg-green-100 text-green-800' },
  summer: { label: 'Summer', frac: 0.5, chip: 'bg-orange-100 text-orange-800' },
};
const seasonType = (s = ''): keyof typeof SEASON_META | null =>
  /fall/i.test(s) ? 'fall' : /winter/i.test(s) ? 'winter' : /spring/i.test(s) ? 'spring' : /summer/i.test(s) ? 'summer' : null;
const seasonSortKey = (s = ''): number => {
  const y = parseInt((s.match(/\d{4}/) || ['0'])[0], 10);
  const t = seasonType(s);
  return y + (t ? SEASON_META[t].frac : 0);
};
// Same real season across sources: USBC labels it "2025-2026", LS labels it "Fall
// 2025" — both start in 2025. Dedupe on start-year + normalized league name.
const seasonStartYear = (s = ''): string => (s.match(/\d{4}/) || ['0'])[0];

const RatingBar: React.FC<{ label: string; value?: number }> = ({ label, value }) => {
  if (value === undefined || value === null) return null;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="font-bold text-willard-grey-700">{label}</span>
        <span className="font-display text-willard-black">{value}/10</span>
      </div>
      <div className="h-2 rounded-full bg-willard-grey-200 overflow-hidden">
        <div className="h-full rounded-full bg-willard-black" style={{ width: `${value * 10}%` }} />
      </div>
    </div>
  );
};

// ---------- page ----------

export const PlayerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { userRole } = useAuth();
  const [player, setPlayer] = useState<Player | null>(null);
  const [stats, setStats] = useState<PlayerGameStats | null>(null);
  const [awards, setAwards] = useState<Award[]>([]);
  const [leagueStats, setLeagueStats] = useState<LeagueStat[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return setState('unavailable');
      try {
        const snap = await getDoc(doc(db, 'players', id));
        if (!snap.exists()) {
          if (!cancelled) setState('unavailable');
          return;
        }
        const p = { id: snap.id, ...snap.data() } as Player;
        if (cancelled) return;
        setPlayer(p);
        setState('ready');

        // Parallel: deep stats + scraped collections. Each fails soft.
        const statsPromise = p.statsPlayerId
          ? calculatePlayerStats(p.statsPlayerId).catch(() => null)
          : Promise.resolve(null);
        const awardsPromise = getDocs(query(collection(db, 'awards'), where('playerId', '==', id)))
          .then((r) => r.docs.map((d) => ({ id: d.id, ...d.data() }) as Award))
          .catch(() => [] as Award[]);
        const lsPromise = getDocs(query(collection(db, 'leagueStats'), where('playerId', '==', id)))
          .then((r) => r.docs.map((d) => ({ id: d.id, ...d.data() }) as LeagueStat))
          .catch(() => [] as LeagueStat[]);

        const [s, a, ls] = await Promise.all([statsPromise, awardsPromise, lsPromise]);
        if (cancelled) return;
        setStats(s);
        setAwards(a.sort((x, y) => (y.season || '').localeCompare(x.season || '')));
        setLeagueStats(
          ls.sort(
            (x, y) =>
              (y.season || '').localeCompare(x.season || '') ||
              Number(x.isComposite || false) - Number(y.isComposite || false) ||
              x.league.localeCompare(y.league)
          )
        );
      } catch {
        // Permission denied = unpublished profile viewed by the public
        if (!cancelled) setState('unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state === 'loading') {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-willard-grey-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-willard-black" />
      </div>
    );
  }

  if (state === 'unavailable' || !player) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 px-4 text-center bg-willard-grey-50">
        <p className="font-display text-3xl text-willard-black">Profile not available</p>
        <p className="text-willard-grey-600 max-w-md">
          This athlete's profile hasn't been published yet. Profiles go live once parental consent is on
          file and the coach publishes them.
        </p>
        <Link to="/roster" className="mt-2 px-6 py-3 bg-willard-black text-white font-bold rounded-lg hover:bg-willard-grey-800 transition-colors">
          Back to Roster
        </Link>
      </div>
    );
  }

  const unpublishedPreview = !(player.published && player.consentOnFile);
  const arsenal = [...(player.ballArsenal || [])].sort(
    (a, b) => (a.bag ?? 1) - (b.bag ?? 1) || a.bagPosition - b.bagPosition
  );
  // Analytics source chain: live Stats-app calc -> synced snapshot -> nothing
  const snap = stats ?? player.statsSnapshot ?? null;
  // Trend source, best first: real league game-by-game (LeagueSecretary weekly
  // scores, includes summer) -> live Stats-app games -> synced snapshot scores.
  const leagueGameScores = (player.recentLeagueGames ?? []).map((g) => g.score).filter((n) => n > 0);
  const trendScores = leagueGameScores.length
    ? leagueGameScores.slice(-21)
    : stats
      ? stats.allGames.slice(-21).map((g) => g.score)
      : (player.statsSnapshot?.recentScores ?? []);
  const trendSource = leagueGameScores.length ? 'league games' : 'tracked games';
  const trend21Avg = trendScores.length ? Math.round(trendScores.reduce((s, v) => s + v, 0) / trendScores.length) : 0;
  const assessment = player.assessment;
  const juniorGold = (player.usbcMemberships || []).find((m) => /junior gold/i.test(m.type));
  const jgResults = (player.juniorGoldResults || []).filter((r) => r.division).sort((a, b) => (b.year || '').localeCompare(a.year || ''));
  const accomplishments = [...(player.accomplishments || [])].sort((a, b) => (b.year || '').localeCompare(a.year || ''));
  const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  };
  const liveLeagues = (player.currentLeagues || []).filter((l) => !l.ambiguous && !l.hidden && l.games > 0);
  // Current season (Fall 2026 / 26-27) leads; the recap season (Summer 2026) stays
  // listed while LeagueSecretary still carries it. Rules: src/config/season.json.
  const seasonLeagues = liveLeagues.filter((l) => isCurrentSeason(l.season));
  const recapLeagues = liveLeagues.filter((l) => isRecapSeason(l.season));
  const summerLeagues = seasonLeagues.length ? [...seasonLeagues, ...recapLeagues] : recapLeagues;
  const bestSummer =
    [...seasonLeagues].sort((a, b) => b.games - a.games)[0] || [...recapLeagues].sort((a, b) => b.games - a.games)[0] || null;
  const liveTitle = seasonLeagues.length ? `${SEASON.current.title}: Live` : `${SEASON.recap.label}: Final`;
  const weeksFor = (league: string) => (player.recentLeagueWeeks || []).filter((w) => w.league === league);
  // Coach display controls per leagueStats row (hide cross-source dupes, flag string
  // pins). Hidden rows are dropped from everything below; the flag is read per row.
  const statControls = player.leagueStatControls || {};
  const isStringPins = (ls: LeagueStat) => !!statControls[ls.id]?.stringPins;
  const visibleLeagueStats = leagueStats.filter((ls) => !statControls[ls.id]?.hidden);
  // Baseline for "vs last season" = most recent completed-season USBC book average
  // (composite leagueStats row), NOT the all-games blend.
  const lastSeasonBook =
    [...visibleLeagueStats]
      .filter((ls) => ls.isComposite && (ls.average || 0) > 0)
      .sort((a, b) => (b.season || '').localeCompare(a.season || ''))[0] || null;
  // Baseline for "vs last season": denormalized last-season average (USBC composite,
  // else most-recent completed LS season) -> composite here -> tracked avg.
  const baselineAvg = player.lastSeasonBookAverage ?? lastSeasonBook?.average ?? snap?.average ?? 0;
  // Career bests across every source (league play + tracked), for the recruiter callout.
  const careerHighGame = Math.max(
    snap?.highGame ?? 0,
    ...visibleLeagueStats.map((l) => l.highGame || 0),
    ...(player.currentLeagues || []).map((l) => l.highGame || 0)
  );
  const careerHighSeries = Math.max(
    snap?.highSeries ?? 0,
    ...visibleLeagueStats.map((l) => l.highSeries || 0),
    ...(player.currentLeagues || []).map((l) => l.highSeries || 0)
  );
  // Team Scoring (Stats-app practice/match data) hidden behind "coming soon" until
  // it reliably reflects each bowler's real season (some kids have partial/absent data).
  const showTeamScoring = false;
  // Season-by-season from LeagueSecretary rows (F/S/W tagged), newest first.
  // Non-composite league rows (LS + USBC), deduped by season+league preferring the
  // row that has a high game (LS year-end beats USBC, which carries no high game).
  // The same league shows up under two names: LeagueSecretary "Junior/Major 25-26"
  // (Fall 2025) vs USBC "Junior/Majors" (2025-2026). Same start year plus either a
  // matching canonical name (letters only, digits and plural s dropped) OR the same
  // games + average (two sources describing one season) = one row. Prefer the row
  // that carries a high game (LS year-end); USBC has average only.
  const seasonRows = (() => {
    const canon = (name = '') =>
      name
        .toLowerCase()
        .replace(/[^a-z\s]+/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.replace(/s$/, ''))
        .join('');
    const kept: LeagueStat[] = [];
    const rows = [...visibleLeagueStats.filter((l) => !l.isComposite && (l.games || 0) > 0)].sort(
      (a, b) => (b.highGame || 0) - (a.highGame || 0)
    );
    for (const ls of rows) {
      const y = seasonStartYear(ls.season);
      const dupe = kept.some(
        (k) =>
          seasonStartYear(k.season) === y &&
          (canon(k.league) === canon(ls.league) ||
            ((k.games || 0) === (ls.games || 0) && Math.round(k.average || 0) === Math.round(ls.average || 0)))
      );
      if (!dupe) kept.push(ls);
    }
    return kept.sort((a, b) => seasonSortKey(b.season) - seasonSortKey(a.season));
  })();
  const lastFallIdx = seasonRows.findIndex((ls) => seasonType(ls.season) === 'fall');

  return (
    <div className="bg-willard-grey-50">
      {unpublishedPreview && (userRole === 'coach' || userRole === 'player') && (
        <div className="bg-willard-taupe text-willard-black text-center text-sm font-bold py-2 px-4">
          PREVIEW: this profile is not publicly visible yet
          {!player.consentOnFile && ' (consent form not on file)'}
          {player.consentOnFile && !player.published && ' (not published)'}
        </div>
      )}

      {/* ---------- Hero ---------- */}
      <section className="bg-willard-black text-white">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div className="flex flex-col md:flex-row gap-10 items-center md:items-end">
            <div className="w-44 h-44 md:w-56 md:h-56 rounded-2xl overflow-hidden bg-willard-grey-800 ring-4 ring-willard-taupe/40 shrink-0">
              {player.photoURL ? (
                <img src={player.photoURL} alt={player.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-willard-grey-500 font-display text-6xl">
                  {player.name?.charAt(0) || '?'}
                </div>
              )}
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="font-heading text-willard-taupe text-xl tracking-[0.3em] uppercase mb-1">
                Willard Tigers Bowling
              </p>
              <h1 className="font-display text-5xl md:text-6xl leading-none mb-4">{player.name}</h1>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 text-sm">
                {player.graduationYear && (
                  <span className="px-3 py-1 rounded-full bg-willard-grey-800 border border-willard-grey-700">
                    Class of {player.graduationYear}
                  </span>
                )}
                {player.hand && (
                  <span className="px-3 py-1 rounded-full bg-willard-grey-800 border border-willard-grey-700">
                    {HAND_LABEL[player.hand] || player.hand}
                  </span>
                )}
                {player.jerseyNumber && (
                  <span className="px-3 py-1 rounded-full bg-willard-grey-800 border border-willard-grey-700">
                    #{player.jerseyNumber}
                  </span>
                )}
                {player.usbcId && (
                  <span className="px-3 py-1 rounded-full bg-willard-taupe text-willard-black font-bold inline-flex items-center gap-1">
                    <ShieldCheck className="w-4 h-4" /> USBC {player.usbcId}
                  </span>
                )}
                {juniorGold && (
                  <span className="px-3 py-1 rounded-full bg-yellow-500 text-willard-black font-bold">
                    Junior Gold
                  </span>
                )}
              </div>
              {player.bio && <p className="mt-5 text-willard-grey-300 max-w-2xl">{player.bio}</p>}
            </div>
          </div>

          {/* Hero stat strip: current summer leads when it exists */}
          {bestSummer ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              <BigStat dark label={avgLabelFor(bestSummer)} value={bestSummer.currentAverage} sub={`${bestSummer.games} games · ${bestSummer.season}`} />
              {baselineAvg > 0 && bestSummer.currentAverage - baselineAvg > 0 ? (
                <BigStat dark label="Improvement" value={`+${Math.round(bestSummer.currentAverage - baselineAvg)}`} sub={lastSeasonBook ? `vs ${lastSeasonBook.season} book` : 'vs last season'} />
              ) : (
                <BigStat dark label="High Series" value={bestSummer.highSeries} sub={bestSummer.league} />
              )}
              <BigStat dark label="High Game" value={careerHighGame || '—'} sub="career best" />
              <BigStat dark label="Last Season Avg" value={baselineAvg || '—'} sub={lastSeasonBook ? `${lastSeasonBook.season} book` : snap ? `${snap.games} games tracked` : ''} />
            </div>
          ) : snap ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              <BigStat dark label="Average" value={snap.average} sub={`${snap.games} games`} />
              <BigStat dark label="High Game" value={snap.highGame} />
              {snap.highSeries > 0 ? (
                <BigStat dark label="High Series" value={snap.highSeries} sub="3 games" />
              ) : (
                <BigStat dark label="Longest Streak" value={snap.longestStreak} sub="games above average" />
              )}
              <BigStat dark label="Last 5 Avg" value={snap.last5Average} sub={snap.last5Average > snap.average ? 'trending up' : 'season pace'} />
            </div>
          ) : (
            (player.averageScore > 0 || player.highGame > 0) && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12">
                <BigStat dark label="Average" value={player.averageScore || '—'} />
                <BigStat dark label="High Game" value={player.highGame || '—'} />
                <BigStat dark label="Games" value={player.gamesPlayed || 0} />
              </div>
            )
          )}

          {/* Trend */}
          {trendScores.length >= 2 && (
            <div className="mt-8 bg-willard-grey-900/70 rounded-xl p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-willard-taupe mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Last {trendScores.length} {trendSource}
                </span>
                <span className="text-willard-grey-300">avg {trend21Avg}</span>
              </p>
              <Sparkline scores={trendScores} />
              <div className="flex justify-between text-[11px] text-willard-grey-400 mt-2 font-bold">
                <span>low {Math.min(...trendScores)}</span>
                <span className="text-willard-taupe">last {trendScores[trendScores.length - 1]}</span>
                <span>high {Math.max(...trendScores)}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-14 space-y-16">
        {/* ---------- Career bests callout ---------- */}
        {(careerHighGame > 0 || careerHighSeries > 0 || (player.leagueGamesOver200 ?? 0) > 0) && (
          <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-willard-black text-white rounded-xl p-6 text-center">
              <p className="font-display text-5xl">{careerHighGame || '—'}</p>
              <p className="text-xs uppercase tracking-widest text-willard-taupe mt-2">Career High Game</p>
            </div>
            <div className="bg-willard-black text-white rounded-xl p-6 text-center">
              <p className="font-display text-5xl">{careerHighSeries || '—'}</p>
              <p className="text-xs uppercase tracking-widest text-willard-taupe mt-2">Career High Series</p>
            </div>
            {(player.leagueGamesOver200 ?? 0) > 0 && (
              <div className="bg-willard-black text-white rounded-xl p-6 text-center col-span-2 md:col-span-1">
                <p className="font-display text-5xl">{player.leagueGamesOver200}</p>
                <p className="text-xs uppercase tracking-widest text-willard-taupe mt-2">200+ Games · {player.leagueTrendSeason || 'this season'}</p>
              </div>
            )}
          </section>
        )}

        {/* ---------- Junior Gold Championships ---------- */}
        {jgResults.length > 0 && (
          <section>
            <SectionTitle icon={<Medal className="w-7 h-7" />}>Junior Gold Championships</SectionTitle>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {jgResults.map((r, i) => (
                <div key={i} className="bg-willard-black text-white rounded-xl p-5 flex items-center gap-4">
                  <div className="shrink-0 w-14 h-14 rounded-full bg-yellow-500 text-willard-black flex items-center justify-center font-display text-xl">
                    {r.place ? ordinal(r.place) : '—'}
                  </div>
                  <div>
                    <p className="font-heading text-xl tracking-wide">{r.division}</p>
                    <p className="text-sm text-willard-grey-400">
                      Junior Gold {r.year}
                      {r.place ? ` · ${ordinal(r.place)} place` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Accomplishments (coach-verified tournament record) ---------- */}
        {accomplishments.length > 0 && (
          <section>
            <SectionTitle icon={<Trophy className="w-7 h-7" />}>Accomplishments</SectionTitle>
            <p className="text-willard-grey-700 mb-4 -mt-2">
              Tournament wins and placements, entered and verified by the coaching staff.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {accomplishments.map((a, i) => {
                const isWin = /^1(st)?$/i.test((a.placement || '').trim()) || /champion|winner/i.test(a.placement || '');
                return (
                  <div key={i} className="bg-white rounded-xl border border-willard-grey-200 p-5 flex items-start gap-4">
                    <div
                      className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                        isWin ? 'bg-yellow-500 text-willard-black' : 'bg-willard-black text-willard-taupe'
                      }`}
                    >
                      <Trophy className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-willard-black">{a.title}</p>
                      <p className="text-sm text-willard-grey-600 mt-0.5">
                        {a.year}
                        {a.placement ? ` · ${a.placement}` : ''}
                        {a.level && (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-willard-grey-100 text-willard-grey-700 text-xs font-bold uppercase tracking-wider">
                            {a.level}
                          </span>
                        )}
                      </p>
                      {a.notes && <p className="text-sm text-willard-grey-600 mt-1.5">{a.notes}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------- Live league play (current season first, recap season after) ---------- */}
        {summerLeagues.length > 0 && (
          <section>
            <SectionTitle icon={<Flame className="w-7 h-7" />}>{liveTitle}</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4">
              {summerLeagues.map((l, i) => {
                  const delta = baselineAvg > 0 ? l.currentAverage - baselineAvg : null;
                  return (
                    <div key={i} className="bg-white rounded-xl shadow-tiger p-5">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold text-willard-black">{l.league}</p>
                          <p className="text-xs text-willard-grey-500">
                            {l.center}{l.dayOfWeek ? ` · ${l.dayOfWeek}s` : ''} · {l.season}
                          </p>
                        </div>
                        {delta !== null && delta > 0 && (
                          <span className="inline-flex items-center gap-1 text-green-600 font-bold text-sm">
                            <TrendingUp className="w-4 h-4" /> +{Math.round(delta)} vs book
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-2 items-end">
                        <div>
                          <p className="font-display text-3xl sm:text-4xl text-willard-black">{l.currentAverage}</p>
                          <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">{isRecapSeason(l.season) ? 'Final Avg' : 'Current Avg'}</p>
                        </div>
                        <div>
                          <p className="font-display text-xl sm:text-2xl text-willard-grey-600">{l.highGame}</p>
                          <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">High Game</p>
                        </div>
                        <div>
                          <p className="font-display text-xl sm:text-2xl text-willard-grey-600">{l.highSeries}</p>
                          <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">High Series</p>
                        </div>
                        <div>
                          <p className="font-display text-xl sm:text-2xl text-willard-grey-600">{l.games}</p>
                          <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">Games</p>
                        </div>
                      </div>
                      {/* The profile is the deep page: every week of the season, open by default. */}
                      <WeekLines weeks={weeksFor(l.league)} tone="light" defaultOpen className="mt-3" />
                    </div>
                  );
                })}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <p className="text-xs text-willard-grey-500">
                Updated nightly from LeagueSecretary. "vs book" compares to {lastSeasonBook ? `the ${lastSeasonBook.season} USBC book average (${lastSeasonBook.average})` : "last season's tracked average"}.
              </p>
            </div>
          </section>
        )}

        {/* ---------- Season by season (LeagueSecretary F/S/W) — league play up front ---------- */}
        {seasonRows.length > 0 && (
          <section>
            <SectionTitle icon={<Target className="w-7 h-7" />}>Season by Season</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4">
              {seasonRows.map((ls, i) => {
                const t = seasonType(ls.season);
                const meta = t ? SEASON_META[t] : { label: 'League', chip: 'bg-willard-grey-100 text-willard-grey-700' };
                const isLastFall = i === lastFallIdx;
                return (
                  <div
                    key={ls.id}
                    className={`bg-white rounded-xl shadow-tiger p-5 ${isLastFall ? 'ring-2 ring-willard-taupe' : ''}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-bold text-willard-black">{ls.league}</p>
                        <p className="text-xs text-willard-grey-500">
                          {ls.center ? `${ls.center} · ` : ''}
                          {ls.season}
                        </p>
                        {isStringPins(ls) && (
                          <span className="inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                            String pins
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${meta.chip}`}>
                        {meta.label}
                        {isLastFall ? ' · latest' : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 items-end">
                      <div>
                        <p className="font-display text-3xl sm:text-4xl text-willard-black">{ls.average}</p>
                        <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">Average</p>
                      </div>
                      <div>
                        <p className="font-display text-xl sm:text-2xl text-willard-grey-600">{ls.highGame || '—'}</p>
                        <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">High Game</p>
                      </div>
                      <div>
                        <p className="font-display text-xl sm:text-2xl text-willard-grey-600">{ls.highSeries || '—'}</p>
                        <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">High Series</p>
                      </div>
                      <div>
                        <p className="font-display text-xl sm:text-2xl text-willard-grey-600">{ls.games}</p>
                        <p className="text-[10px] uppercase tracking-wider text-willard-grey-500">Games</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-willard-grey-500 mt-3">
              Season type (Fall / Summer / Winter) comes straight from LeagueSecretary. Frame-level detail (strike %,
              streaks) is in Team Scoring below.
            </p>
          </section>
        )}

        {/* ---------- Team scoring — coming soon ---------- */}
        <section>
          <SectionTitle icon={<BarChart3 className="w-7 h-7" />}>Team Scoring</SectionTitle>
          <div className="bg-white rounded-xl shadow-tiger p-8 text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-willard-grey-400 bg-willard-grey-100 rounded-full px-3 py-1 mb-3">
              Coming soon
            </span>
            <p className="text-sm text-willard-grey-500 max-w-md mx-auto">
              Practice &amp; match scoring will live here once it reflects each bowler's real season.
            </p>
          </div>
        </section>

        {/* Detailed Stats-app breakdown — hidden until it reflects real play (showTeamScoring) */}
        {showTeamScoring && snap && (
          <section>
            <SectionTitle icon={<BarChart3 className="w-7 h-7" />}>Team Scoring: Practice &amp; Matches</SectionTitle>
            <p className="text-sm text-willard-grey-500 -mt-3 mb-4">
              From the team's score tracker (practice and matches), separate from sanctioned league play. League
              averages are up top in the live league section and Season by Season.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <BigStat label="Tracked Avg" value={snap.average} sub={`${snap.games} games · practice + matches`} />
              <BigStat label="High Game" value={snap.highGame} />
              <BigStat label="Bronze / Silver / Gold" value={`${snap.gamesOver25 + snap.seriesOver25} / ${snap.gamesOver50 + snap.seriesOver50} / ${snap.gamesOver100 + snap.seriesOver100}`} sub="USBC over-average" />
              {(player.statsSnapshot?.improvement ?? 0) > 0 ? (
                <BigStat label="In-Season Gain" value={`+${player.statsSnapshot!.improvement}`} sub="2nd half vs 1st half" />
              ) : (
                <BigStat label="Total Pinfall" value={snap.totalPins.toLocaleString()} />
              )}
            </div>
            <details className="bg-white rounded-xl shadow-tiger group">
              <summary className="cursor-pointer list-none px-5 py-4 font-bold text-willard-black flex justify-between items-center">
                Full season breakdown
                <span className="text-willard-grey-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <div className="px-5 pb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Frame-level data only exists when the Stats app recorded it; zeros mean absent, not bad */}
              {snap.strikes + snap.spares > 0 && (
                <>
                  <BigStat label="Strike %" value={`${snap.strikePercentage}%`} />
                  <BigStat label="Spare Conversion" value={`${snap.sparePercentage}%`} />
                </>
              )}
              <BigStat label="Hot Streak" value={snap.currentStreak} sub={`best: ${snap.longestStreak} above avg`} />
              <BigStat label="Total Pinfall" value={snap.totalPins.toLocaleString()} />
              {snap.strikes + snap.spares === 0 && (
                <>
                  <BigStat label="Low Game" value={snap.lowGame} />
                  <BigStat label="Season Games" value={snap.games} />
                </>
              )}
            </div>

            {/* USBC over-average medals */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              {[
                { tier: 'Bronze', games: snap.gamesOver25, series: snap.seriesOver25, note: '25+ over average' },
                { tier: 'Silver', games: snap.gamesOver50, series: snap.seriesOver50, note: '50+ over average' },
                { tier: 'Gold', games: snap.gamesOver100, series: snap.seriesOver100, note: '100+ over average' },
              ].map((m) => (
                <div key={m.tier} className="bg-white rounded-xl shadow-tiger p-5 text-center">
                  <Medal
                    className={`w-8 h-8 mx-auto mb-2 ${
                      m.tier === 'Gold' ? 'text-yellow-500' : m.tier === 'Silver' ? 'text-willard-grey-400' : 'text-amber-700'
                    }`}
                  />
                  <p className="font-heading text-xl tracking-wide">{m.tier}</p>
                  <p className="font-display text-3xl text-willard-black">{m.games + m.series}</p>
                  <p className="text-xs text-willard-grey-500">
                    {m.games} games, {m.series} series {m.note}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-willard-grey-500 mt-3">
              Live from the team's <a className="underline" href="https://bowlingstatstracker.web.app" target="_blank" rel="noreferrer">Stats Tracker</a>, every frame of every game is recorded and verifiable.
            </p>
              </div>
            </details>
          </section>
        )}

        {/* ---------- USBC career (collapsed) ---------- */}
        {(awards.length > 0 || visibleLeagueStats.length > 0) && (
          <section>
            <SectionTitle icon={<Medal className="w-7 h-7" />}>Verified USBC Record</SectionTitle>
            {visibleLeagueStats.some((ls) => ls.isComposite) && (
              <p className="text-willard-grey-700 mb-4">
                Official book average by season:{' '}
                <span className="font-bold">
                  {visibleLeagueStats
                    .filter((ls) => ls.isComposite)
                    .sort((a, b) => (a.season || '').localeCompare(b.season || ''))
                    .map((ls) => ls.average)
                    .join(' → ')}
                </span>
              </p>
            )}
            <details className="bg-white rounded-xl shadow-tiger group mb-2">
              <summary className="cursor-pointer list-none px-5 py-4 font-bold text-willard-black flex justify-between items-center">
                Full USBC career history
                <span className="text-willard-grey-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <div className="px-5 pb-6">
            <div className="grid md:grid-cols-2 gap-8">
              {awards.length > 0 && (
                <div>
                  <h3 className="font-bold text-willard-grey-700 mb-3">Awards</h3>
                  <ul className="space-y-2">
                    {awards.map((a) => (
                      <li key={a.id} className="bg-white rounded-lg shadow-tiger px-4 py-3 flex justify-between items-center">
                        <span className="font-bold text-willard-black">{a.title}</span>
                        <span className="text-sm text-willard-grey-500">{a.season}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {visibleLeagueStats.length > 0 && (
                <div>
                  <h3 className="font-bold text-willard-grey-700 mb-3">League History</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg shadow-tiger text-sm">
                      <thead>
                        <tr className="text-left text-willard-grey-500 border-b border-willard-grey-200">
                          <th className="px-4 py-2">Season</th>
                          <th className="px-4 py-2">League</th>
                          <th className="px-4 py-2 text-right">Avg</th>
                          <th className="px-4 py-2 text-right">Games</th>
                          <th className="px-4 py-2">Center</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleLeagueStats.map((ls) => (
                          <tr
                            key={ls.id}
                            className={`border-b border-willard-grey-100 last:border-0 ${ls.isComposite ? 'bg-willard-grey-50 font-bold' : ''}`}
                          >
                            <td className="px-4 py-2">{ls.season}</td>
                            <td className="px-4 py-2">
                              {ls.league}
                              {isStringPins(ls) && (
                                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800 align-middle">
                                  string pins
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right font-bold">{ls.average}</td>
                            <td className="px-4 py-2 text-right">{ls.games}</td>
                            <td className="px-4 py-2 text-willard-grey-500">{ls.center || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-willard-grey-500 mt-2">
                    Composite rows are the official USBC book average for that season.
                  </p>
                </div>
              )}
            </div>
              </div>
            </details>
          </section>
        )}

        {/* ---------- Arsenal, grouped by physical bag ---------- */}
        {arsenal.length > 0 && (
          <section>
            <SectionTitle icon={<CircleDot className="w-7 h-7" />}>
              {new Set(arsenal.map((b) => b.bag ?? 1)).size > 1 ? 'The Bags' : 'The Bag'}
            </SectionTitle>
            {[...new Set(arsenal.map((b) => b.bag ?? 1))].sort((a, b) => a - b).map((bagNum) => (
              <div key={bagNum} className="mb-8 last:mb-0">
                {new Set(arsenal.map((b) => b.bag ?? 1)).size > 1 && (
                  <p className="text-sm font-bold uppercase tracking-widest text-willard-grey-500 mb-3">
                    Bag {bagNum}
                  </p>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {arsenal.filter((b) => (b.bag ?? 1) === bagNum).map((ball, i) => (
                <div key={i} className="bg-white rounded-xl shadow-tiger p-5 relative overflow-hidden">
                  <span className="absolute -top-3 -right-2 font-display text-7xl text-willard-grey-100 select-none">
                    {ball.bagPosition}
                  </span>
                  {ball.imageUrl ? (
                    <img
                      src={ball.imageUrl}
                      alt={`${ball.brand} ${ball.name}`}
                      className="w-24 h-24 object-contain mb-3 relative"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-willard-black flex items-center justify-center mb-3 relative">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-willard-taupe text-center px-2">
                        {ball.brand}
                      </span>
                    </div>
                  )}
                  <p className="text-xs font-bold uppercase tracking-widest text-willard-grey-500">{ball.brand}</p>
                  <p className="font-display text-2xl text-willard-black mb-2">{ball.name}</p>
                  <span className="inline-block text-xs font-bold px-2 py-1 rounded-full bg-willard-black text-white mb-3">
                    {ROLE_LABEL[ball.role] || ball.role}
                  </span>
                  <dl className="text-sm space-y-1 relative">
                    <div className="flex justify-between gap-2">
                      <dt className="text-willard-grey-500">Coverstock</dt>
                      <dd className="font-bold text-right">{ball.coverstock}</dd>
                    </div>
                    {ball.core && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-willard-grey-500">Core</dt>
                        <dd className="font-bold text-right">{ball.core}</dd>
                      </div>
                    )}
                    {ball.surface && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-willard-grey-500">Surface</dt>
                        <dd className="font-bold text-right">{ball.surface}</dd>
                      </div>
                    )}
                  </dl>
                  {ball.notes && <p className="text-xs text-willard-grey-500 mt-3">{ball.notes}</p>}
                </div>
              ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* ---------- Coach assessment ---------- */}
        {assessment && (
          <section>
            <SectionTitle icon={<Flame className="w-7 h-7" />}>Coach's Assessment</SectionTitle>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-tiger p-6 space-y-4">
                <h3 className="font-heading text-xl tracking-wide">Physical Game</h3>
                <RatingBar label="Balance" value={assessment.physicalGame?.balance} />
                <RatingBar label="Timing" value={assessment.physicalGame?.timing} />
                <RatingBar label="Release" value={assessment.physicalGame?.release} />
                <RatingBar label="Footwork" value={assessment.physicalGame?.footwork} />
                <RatingBar label="Arm Swing" value={assessment.physicalGame?.armSwing} />
                <RatingBar label="Versatility" value={assessment.physicalGame?.versatility} />
              </div>
              <div className="bg-white rounded-xl shadow-tiger p-6 space-y-4">
                <h3 className="font-heading text-xl tracking-wide">Mental Game</h3>
                <RatingBar label="Composure" value={assessment.mentalGame?.composure} />
                <RatingBar label="Lane Play & Strategy" value={assessment.mentalGame?.strategy} />
                <RatingBar label="Spare System" value={assessment.mentalGame?.sparesystem} />
                <RatingBar label="Coachability" value={assessment.mentalGame?.coachability} />
              </div>
            </div>
            {assessment.summary && (
              <blockquote className="mt-6 bg-willard-black text-white rounded-xl p-6">
                <p className="italic text-willard-grey-200">"{assessment.summary}"</p>
                {assessment.projection && (
                  <p className="mt-3 text-sm text-willard-taupe font-bold uppercase tracking-widest">
                    Projection: {assessment.projection}
                  </p>
                )}
                <footer className="mt-2 text-sm text-willard-grey-400">Coach Collins</footer>
              </blockquote>
            )}
          </section>
        )}

        {/* ---------- Video ---------- */}
        {(player.videoLinks || []).length > 0 && (
          <section>
            <SectionTitle icon={<Video className="w-7 h-7" />}>Film Room</SectionTitle>
            <div className="grid md:grid-cols-2 gap-6">
              {(player.videoLinks || []).map((v, i) => {
                const embed = youtubeEmbed(v.url);
                return (
                  <div key={i} className="bg-white rounded-xl shadow-tiger overflow-hidden">
                    {embed ? (
                      <iframe
                        src={embed}
                        title={v.title}
                        className="w-full aspect-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video src={v.url} controls className="w-full aspect-video bg-black" />
                    )}
                    <div className="px-4 py-3 flex justify-between items-center">
                      <p className="font-bold text-willard-black">{v.title}</p>
                      <span className="text-xs text-willard-grey-500 uppercase tracking-widest">{v.type}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------- Academics + goals ---------- */}
        {(player.academics || player.goals) && (
          <section>
            <SectionTitle icon={<GraduationCap className="w-7 h-7" />}>Student Athlete</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-4">
              {player.academics?.gpa !== undefined && <BigStat label="GPA" value={player.academics.gpa.toFixed(2)} />}
              {player.academics?.actSat && <BigStat label="ACT / SAT" value={player.academics.actSat} />}
              {player.academics?.intendedMajor && <BigStat label="Intended Major" value={<span className="text-2xl">{player.academics.intendedMajor}</span>} />}
            </div>
            {player.goals && (
              <div className="mt-4 bg-white rounded-xl shadow-tiger p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-willard-grey-500 mb-2">Goals</p>
                <p className="text-willard-grey-700">{player.goals}</p>
              </div>
            )}
          </section>
        )}

        {/* ---------- FAQ ---------- */}
        {(player.faq || []).length > 0 && (
          <section>
            <SectionTitle icon={<MessageCircleQuestion className="w-7 h-7" />}>Get To Know {player.name?.split(' ')[0]}</SectionTitle>
            <div className="space-y-3">
              {(player.faq || []).map((f, i) => (
                <details key={i} className="bg-white rounded-xl shadow-tiger group">
                  <summary className="cursor-pointer list-none px-5 py-4 font-bold text-willard-black flex justify-between items-center">
                    {f.question}
                    <span className="text-willard-grey-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="px-5 pb-4 text-willard-grey-600">{f.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Recruiter CTA ---------- */}
        <section className="bg-willard-black text-white rounded-2xl p-10 text-center">
          <h2 className="font-display text-3xl mb-3">Recruiting {player.name?.split(' ')[0]}?</h2>
          <p className="text-willard-grey-300 max-w-xl mx-auto mb-6">
            All recruiter contact routes through the coaching staff. We'll respond with verified stats,
            full game film, and anything else your program needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-willard-taupe text-willard-black font-bold rounded-lg hover:bg-white transition-colors"
          >
            <Mail className="w-5 h-5" /> Contact Coach Collins
          </Link>
        </section>
      </div>
    </div>
  );
};
